"use client";

/**
 * ListPage context
 * Provides shared state and configuration to all list page components
 */

import { createContext, useContext } from "react";
import type {
  ListPageContextValue,
  ListPageItem,
  ListPageConfig,
  ListPageState,
  ListPageActions,
  ListPagePermissions,
} from "./types";

// ============================================================================
// Context
// ============================================================================

const ListPageContext = createContext<ListPageContextValue<
  ListPageItem,
  string,
  string
> | null>(null);

// ============================================================================
// Provider Props
// ============================================================================

export interface ListPageProviderProps<
  TData extends ListPageItem = ListPageItem,
  TViewType extends string = string,
  TGroupByField extends string = string,
> {
  /** Page configuration */
  config: ListPageConfig<TViewType, TGroupByField>;
  /** Current state */
  state: ListPageState<TViewType, TGroupByField>;
  /** State actions */
  actions: ListPageActions<TViewType, TGroupByField>;
  /** Permission helpers */
  permissions: ListPagePermissions;
  /** Data items */
  data: TData[];
  /** Total count (for pagination) */
  total?: number;
  /** Loading state */
  isLoading: boolean;
  /** Fetching state (background refresh) */
  isFetching?: boolean;
  /** Children */
  children: React.ReactNode;
}

// ============================================================================
// Provider
// ============================================================================

export function ListPageProvider<
  TData extends ListPageItem = ListPageItem,
  TViewType extends string = string,
  TGroupByField extends string = string,
>({
  config,
  state,
  actions,
  permissions,
  data,
  total,
  isLoading,
  isFetching,
  children,
}: ListPageProviderProps<TData, TViewType, TGroupByField>) {
  const value: ListPageContextValue<TData, TViewType, TGroupByField> = {
    config,
    state,
    actions,
    permissions,
    data,
    total,
    isLoading,
    isFetching,
  };

  return (
    <ListPageContext.Provider
      value={
        value as unknown as ListPageContextValue<ListPageItem, string, string>
      }
    >
      {children}
    </ListPageContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useListPage<
  TData extends ListPageItem = ListPageItem,
  TViewType extends string = string,
  TGroupByField extends string = string,
>(): ListPageContextValue<TData, TViewType, TGroupByField> {
  const context = useContext(ListPageContext);

  if (!context) {
    throw new Error("useListPage must be used within a ListPageProvider");
  }

  return context as unknown as ListPageContextValue<
    TData,
    TViewType,
    TGroupByField
  >;
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Get the list page configuration
 */
export function useListPageConfig<
  TViewType extends string = string,
  TGroupByField extends string = string,
>(): ListPageConfig<TViewType, TGroupByField> {
  const { config } = useListPage();
  return config as ListPageConfig<TViewType, TGroupByField>;
}

/**
 * Get the list page state
 */
export function useListPageContextState<
  TViewType extends string = string,
  TGroupByField extends string = string,
>(): ListPageState<TViewType, TGroupByField> {
  const { state } = useListPage();
  return state as ListPageState<TViewType, TGroupByField>;
}

/**
 * Get the list page actions
 */
export function useListPageActions<
  TViewType extends string = string,
  TGroupByField extends string = string,
>(): ListPageActions<TViewType, TGroupByField> {
  const { actions } = useListPage();
  return actions as ListPageActions<TViewType, TGroupByField>;
}

/**
 * Get the list page permissions
 */
export function useListPagePermissions(): ListPagePermissions {
  const { permissions } = useListPage();
  return permissions;
}

/**
 * Get the list page data
 */
export function useListPageData<TData extends ListPageItem = ListPageItem>(): {
  data: TData[];
  total?: number;
  isLoading: boolean;
  isFetching?: boolean;
} {
  const { data, total, isLoading, isFetching } = useListPage<TData>();
  return { data, total, isLoading, isFetching };
}
